import { consola } from 'consola'
import { apiSupportChat } from '../api/apiSupportChat'

/**
 * Стикеры чата поддержки. Два источника, и это осознанно:
 *
 *  - ВСТРОЕННЫЙ пакет (`noto.*`) лежит в сборке фронта (public/stickers/noto).
 *    Он одинаков для всех брендов, работает офлайн и не стоит ни запроса, ни S3.
 *  - ПАКЕТЫ ЗАВЕДЕНИЯ (`p<packId>.<stickerId>`) заведение загружает себе само
 *    через админку. Мультибренд: сервер отдаёт только пакеты того заведения,
 *    к которому относится сессия, и он же проверяет принадлежность при отправке.
 *
 * Сообщение хранит короткий id. Клиент старой сборки, не знающий id, рисует
 * заглушку — поэтому id, однажды попавший в чат, не переименовываем.
 *
 * Анимации встроенного пакета — Noto Animated Emoji (Google, CC BY 4.0),
 * см. public/stickers/noto/LICENSE.txt.
 */

export interface SupportSticker {
  /** id для сервера и истории */
  id: string
  /** Подпись для screen reader и заглушки */
  alt: string
  /** Ссылка на файл: путь в public (встроенный) или URL из S3 (пакет заведения) */
  src: string
  /** Lottie рисуется плеером, картинка — обычным <img> */
  kind: 'lottie' | 'image'
}

export interface SupportStickerPack {
  id: string
  title: string
  stickers: SupportSticker[]
}

/** name → человекочитаемая подпись; порядок задаёт вид сетки в пикере */
const NOTO_STICKERS: Array<[name: string, alt: string]> = [
  ['thumbs_up', 'Класс'],
  ['heart', 'Сердце'],
  ['fire', 'Огонь'],
  ['joy', 'Смех'],
  ['rofl', 'Хохот'],
  ['heart_eyes', 'Восторг'],
  ['cool', 'Круто'],
  ['ok_hand', 'Окей'],
  ['clap', 'Аплодисменты'],
  ['party', 'Праздник'],
  ['pray', 'Спасибо'],
  ['thinking', 'Думаю'],
  ['eyes', 'Смотрю'],
  ['sleeping', 'Сплю'],
  ['cry', 'Грустно'],
  ['rage', 'Злюсь'],
  ['check', 'Готово'],
  ['cross', 'Нет'],
  ['alarm', 'Ждём'],
  ['rocket', 'Летим'],
  ['pizza', 'Пицца'],
  ['burger', 'Бургер'],
  ['cake', 'Тортик'],
  ['coffee', 'Кофе'],
]

export const BUILTIN_STICKER_PACK: SupportStickerPack = {
  id: 'noto',
  title: 'Базовые',
  stickers: NOTO_STICKERS.map(([name, alt]) => ({
    id: `noto.${name}`,
    alt,
    src: `/stickers/noto/${name}.json`,
    kind: 'lottie' as const,
  })),
}

// Состояние на уровне модуля: пакеты заведения нужны и пикеру, и любому пузырю
// со стикером, а грузить манифест по разу на компонент незачем
const institutionPacks = ref<SupportStickerPack[]>([])
const isLoaded = ref(false)
let inFlight: Promise<void> | null = null
/**
 * Поколение состояния: reset() его инкрементирует, и долетевший ответ запроса,
 * начатого ДО сброса, отбрасывается — иначе логаут во время load() дозаполнил бы
 * стор пакетами, полученными под прошлой сессией.
 */
let generation = 0

const byId = computed(() => {
  const map = new Map<string, SupportSticker>()
  for (const sticker of BUILTIN_STICKER_PACK.stickers) map.set(sticker.id, sticker)
  for (const pack of institutionPacks.value) {
    for (const sticker of pack.stickers) map.set(sticker.id, sticker)
  }
  return map
})

/** null — стикера из этого id нет (старый пакет, чужая сборка, выключён в админке) */
export function findSticker(id: string | null | undefined): SupportSticker | null {
  if (!id) return null
  return byId.value.get(id) ?? null
}

export function useStickerPacks() {
  /** Свои пакеты выше встроенного: бренд показывает своё первым */
  const packs = computed<SupportStickerPack[]>(() => [
    ...institutionPacks.value,
    BUILTIN_STICKER_PACK,
  ])

  /**
   * Подтянуть пакеты заведения. Повторный вызов не шлёт второй запрос:
   * пикер открывают и закрывают часто, а манифест за сессию не меняется.
   */
  function load(): Promise<void> {
    if (isLoaded.value) return Promise.resolve()
    if (inFlight) return inFlight

    const startedFor = generation
    inFlight = apiSupportChat
      .getStickerPacks()
      .then((loaded) => {
        if (startedFor !== generation) return
        institutionPacks.value = loaded
        isLoaded.value = true
      })
      .catch((error) => {
        // Пакетов заведения может не быть вовсе — встроенный всё равно работает
        consola.withTag('support-chat').warn('Не удалось загрузить стикеры заведения:', error)
      })
      .finally(() => {
        inFlight = null
      })

    return inFlight
  }

  /** Сброс при смене личности/заведения — иначе чужие пакеты останутся в памяти */
  function reset(): void {
    generation += 1
    inFlight = null
    institutionPacks.value = []
    isLoaded.value = false
  }

  return { packs, load, reset, findSticker }
}
